// DISPLAY SIZE — the whole console, larger, for a screen read from across a room.
//
// Casting a tab to a TV mirrors the laptop's pixels: the TV shows the same
// 1300px page scaled up, so 11px captions stay 11/1300ths of the screen and an
// audience four metres away reads nothing (Jeff, 2026-09-27). Browser zoom
// (⌘+) reflows correctly, but it is not discoverable and a TV browser has no
// keyboard. This is the in-app equivalent.
//
// It works by CSS `zoom` on <html>, which scales every length — text, controls,
// borders, icons — and three things that `zoom` alone gets wrong are corrected
// where they happen:
//   - Media queries ignore zoom, so a 1440px window at 150% would keep the
//     1400px-and-up layout with 960px of room. postcss-display-zoom.cjs copies
//     every width query once per level with its breakpoints scaled, guarded by
//     html[data-zoom]. (Container queries already see the zoomed size.)
//   - `100vh` is zoomed too (900 → 1350). The same build step divides every
//     viewport unit by --display-zoom.
//   - Positions a script computes from the pointer or getBoundingClientRect are
//     in SCREEN pixels, and a fixed element's `left` is in zoomed CSS pixels.
//     `toCss()` converts; every site that writes one uses it.
//
// Set from Settings › Display (features/common/DisplaySizeControl.tsx) or the
// keyboard. Remembered per browser (a projector laptop keeps its size), and
// settable from the address bar — `?display=150` — so a TV's bookmark opens large.

import { useEffect, useState } from "react";

/** The levels a display can take. The build step emits breakpoints for exactly these. */
export const DISPLAY_LEVELS = [1, 1.25, 1.5, 2] as const;
export type DisplayLevel = (typeof DISPLAY_LEVELS)[number];

export const DISPLAY_ZOOM_KEY = "soc.displayZoom";
const CHANGE = "soc:display-zoom";

function asLevel(value: unknown): DisplayLevel {
  const n = Number(value);
  // `?display=150` and a stored 1.5 are the same request.
  const scaled = n > 10 ? n / 100 : n;
  return (DISPLAY_LEVELS as readonly number[]).includes(scaled) ? (scaled as DisplayLevel) : 1;
}

/** The level in force on this document. */
export function displayZoom(): DisplayLevel {
  if (typeof document === "undefined") return 1;
  return asLevel(document.documentElement.dataset.zoom ?? 1);
}

function stored(): DisplayLevel {
  try {
    return asLevel(window.localStorage.getItem(DISPLAY_ZOOM_KEY) ?? 1);
  } catch {
    return 1;
  }
}

function apply(level: DisplayLevel): void {
  const html = document.documentElement;
  if (level === 1) {
    html.style.removeProperty("zoom");
    html.style.removeProperty("--display-zoom");
    delete html.dataset.zoom;
  } else {
    html.style.setProperty("zoom", String(level));
    html.style.setProperty("--display-zoom", String(level));
    html.dataset.zoom = String(level);
  }
}

/** Set, remember and announce a level. */
export function setDisplayZoom(level: DisplayLevel): void {
  apply(level);
  try {
    if (level === 1) window.localStorage.removeItem(DISPLAY_ZOOM_KEY);
    else window.localStorage.setItem(DISPLAY_ZOOM_KEY, String(level));
  } catch {
    // A private window or blocked storage: the size still applies to this page.
  }
  window.dispatchEvent(new CustomEvent(CHANGE, { detail: level }));
}

/** One step up or down the levels, clamped at the ends. */
export function stepDisplayZoom(direction: 1 | -1): void {
  const at = DISPLAY_LEVELS.indexOf(displayZoom());
  const next = DISPLAY_LEVELS[Math.max(0, Math.min(DISPLAY_LEVELS.length - 1, at + direction))];
  if (next !== displayZoom()) setDisplayZoom(next);
}

/**
 * Apply the remembered (or address-bar) level before React mounts, so the first
 * paint is already the right size, and install the keyboard shortcut:
 * Alt+Shift+= larger, Alt+Shift+- smaller, Alt+Shift+0 back to 100%. By
 * `event.code`, because on a Mac Alt changes the character the key types.
 */
export function initDisplayZoom(): void {
  if (typeof window === "undefined") return;
  const fromUrl = new URLSearchParams(window.location.search).get("display");
  if (fromUrl !== null) setDisplayZoom(asLevel(fromUrl));
  else apply(stored());
  window.addEventListener("keydown", (event) => {
    if (!event.altKey || !event.shiftKey || event.ctrlKey || event.metaKey) return;
    if (event.code === "Equal" || event.code === "NumpadAdd") stepDisplayZoom(1);
    else if (event.code === "Minus" || event.code === "NumpadSubtract") stepDisplayZoom(-1);
    else if (event.code === "Digit0" || event.code === "Numpad0") setDisplayZoom(1);
    else return;
    event.preventDefault();
  });
}

/** The level, re-rendering when it changes. */
export function useDisplayZoom(): DisplayLevel {
  const [level, setLevel] = useState<DisplayLevel>(() => displayZoom());
  useEffect(() => {
    const on = () => setLevel(displayZoom());
    window.addEventListener(CHANGE, on);
    return () => window.removeEventListener(CHANGE, on);
  }, []);
  return level;
}

/**
 * A SCREEN-pixel length (from the pointer, getBoundingClientRect or
 * innerWidth) as the CSS length that draws it there under the current zoom.
 * At 100% it is the identity.
 */
export function toCss(screenPx: number): number {
  return screenPx / displayZoom();
}

/** The viewport in CSS pixels — what a fixed element can actually span. */
export function viewportCss(): { width: number; height: number } {
  const z = displayZoom();
  return { width: window.innerWidth / z, height: window.innerHeight / z };
}

/** Call `cb` whenever the display size changes. Returns the unsubscribe. */
export function onDisplayZoomChange(cb: () => void): () => void {
  window.addEventListener(CHANGE, cb);
  return () => window.removeEventListener(CHANGE, cb);
}
